"use client";

import { ChevronsUpDown, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { useAuth } from "@/core/context/AuthContext";
import { useTranslation } from "react-i18next";
import { useSelector } from "@/core/redux/store";
import { Skeleton } from "./skeleton";

export function NavUser({
  user,
  absoluteProfile = false,
  skeleton = false,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
  };
  absoluteProfile?: boolean;
  skeleton?: boolean;
}) {
  const me = useSelector((state) => state.user.me);
  const { isMobile } = useSidebar();
  const { logout } = useAuth();
  const { t } = useTranslation();
  const sidebarIsOpen = useSelector((state) => state.sidebar.isOpen);
  const name =
    me?.firstName && me?.lastName
      ? me?.firstName + " " + me?.lastName || ""
      : null;

  return (
    <SidebarMenu
      className={` w-full ${
        absoluteProfile
          ? sidebarIsOpen
            ? "absolute left-0 bottom-[8px]"
            : "absolute left-2 bottom-[8px]"
          : ""
      }`}
    >
      <SidebarMenuItem>
        {!skeleton && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="">
              <SidebarMenuButton
                size="lg"
                className={`data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground`}
              >
                <Avatar className="rounded-full max-w-[32px] max-h-[32px]">
                  <AvatarImage
                    src={
                      me?.profilePictureUrl || "https://github.com/shadcn.png"
                    }
                    alt={me?.firstName || ""}
                    className="rounded-full object-cover"
                  />
                  <AvatarFallback className="rounded-lg">
                    {me?.firstName?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-700">
                    {name ? name : user.name}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm ">
                  <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage
                      src={
                        me?.profilePictureUrl || "https://github.com/shadcn.png"
                      }
                      alt={me?.firstName || ""}
                      className="rounded-full object-cover"
                    />
                    <AvatarFallback className="rounded-lg">
                      {user.email.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-600 text-zinc-700 text-sm">
                      {name ? name : user.name}
                    </span>
                    <span className="truncate font-500 text-xs text-zinc-500">
                      {user.email}
                    </span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* <DropdownMenuGroup>
              <DropdownMenuItem>
                <BadgeCheck />
                item 1
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator /> */}
              {/* <DropdownMenuGroup>
              <DropdownMenuItem>
                <BadgeCheck />
                item 2
              </DropdownMenuItem>
              <DropdownMenuItem>
                <BadgeCheck />
                item 3
              </DropdownMenuItem>
              <DropdownMenuItem>
                <BadgeCheck />
                item 4
              </DropdownMenuItem>
            </DropdownMenuGroup> */}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  logout();
                }}
              >
                <LogOut />
                {t("log_out")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {skeleton && (
          <div className="flex items-center justify-between gap-2    ">
            <Skeleton className="h-8 w-8 bg-zinc-100 rounded-[6px]" />
            <div className="flex flex-col gap-1 w-full">
              <Skeleton className="w-4/6 h-5 bg-zinc-100 rounded-[6px]" />
              <Skeleton className="w-4/6 h-5 bg-zinc-100 rounded-[6px]" />
            </div>
            <Skeleton className="h-5 w-5 bg-zinc-100 rounded-[6px]" />
          </div>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
